import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { api, ApiError } from '@/lib/api';
import { useNoIndex } from '@/lib/useNoIndex';
import {
  InstitutionStudyReportView,
  type SharedInstitutionStudyReport,
} from './InstitutionStudyReportView';

function unavailableMessage(error: unknown): string {
  if (error instanceof ApiError && error.code === 'STUDY_REPORT_SHARE_REVOKED') {
    return 'The school has withdrawn this report link. Ask the school for a new link.';
  }
  if (error instanceof ApiError && error.code === 'STUDY_REPORT_SHARE_EXPIRED') {
    return 'This report link has expired. Ask the school for a new link.';
  }
  return 'This report link is not valid. Check that you copied the whole link.';
}

/**
 * Public parent view of a shared Institution weekly report (`/portal/study/shared/:token`,
 * SIR-VIS-05). No sign-in; the page is never indexed. The server answers 410 once the school
 * revokes the link or it expires.
 */
export function SharedStudyReportPage() {
  const { token = '' } = useParams<{ token: string }>();
  useNoIndex();
  const report = useQuery({
    queryKey: ['shared-institution-study-report', token],
    queryFn: () =>
      api<SharedInstitutionStudyReport>(
        `/public/study/report-shares/${encodeURIComponent(token)}`,
        {
          skipAuthRefresh: true,
        },
      ),
    enabled: token !== '',
    retry: false,
  });

  return (
    <main className="mx-auto w-full max-w-[880px] px-4 py-8 sm:py-12">
      {report.isLoading && <p className="lead-text">Opening the Study report…</p>}
      {report.isError && (
        <section className="card-base" role="alert">
          <h1 className="section-heading">Report not available</h1>
          <p className="lead-text mt-3">{unavailableMessage(report.error)}</p>
        </section>
      )}
      {report.data && <InstitutionStudyReportView report={report.data} />}
    </main>
  );
}
